import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { isSessionRestoreEnabled } from "./sessionRestore/sessionRestore.config";
import { sessionRestoreService } from "./sessionRestore/SessionRestoreService";

/**
 * Opens and closes the Payrexx hosted checkout.
 *
 * On web the shopper leaves the app entirely (`window.location.assign`), so all
 * in-memory purchase state is lost — recovery goes through the backend bounce +
 * `PayrexxReturnPage` resume. On native the checkout opens in the in-app
 * browser; the `flowride://` deep link or a manual close brings the shopper
 * back (see `PayrexxNativeReturnBootstrap`).
 */
export class PayrexxCheckoutService {

    /**
     * Whether the current runtime is a Capacitor native shell.
     *
     * @returns `true` on iOS/Android builds.
     */
    public static isNativeCheckout(): boolean {
        return Capacitor.isNativePlatform();
    }

    /**
     * Opens the Payrexx hosted payment page.
     *
     * Web: full-page navigation to `url` (the app reloads on return).
     * Native: opens `url` in the Capacitor in-app browser.
     *
     * @param url - `redirectUrl` returned by `POST /api/purchase/payment/gateway`.
     */
    public static async openCheckout( url: string ): Promise<void> {
        if ( PayrexxCheckoutService.isNativeCheckout() ) {
            await Browser.open( { url } );

            return;
        }

        /*
         * The full-page redirect drops all in-memory state: persist the planner
         * session and map camera so SessionRestoreBootstrap can re-apply them
         * when the Payrexx return lands back on /home.
         */
        if ( isSessionRestoreEnabled() ) {
            sessionRestoreService.saveAppState();
        }

        window.location.assign( url );
    }

    /**
     * Closes the in-app checkout browser on native; no-op on web.
     */
    public static async closeCheckout(): Promise<void> {
        if ( PayrexxCheckoutService.isNativeCheckout() ) {
            await Browser.close().catch( () => {
                // No browser open (already dismissed by the deep link): nothing to close.
            } );
        }
    }

}
